import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext";

type MultiSelectOption = {
  id: number;
  label: string;
};

type MultiSelectFieldProps = {
  label: string;
  value: MultiSelectOption[];
  onChange: (value: MultiSelectOption[]) => void;
  options: MultiSelectOption[];

  placeholder?: string;
  style?: React.CSSProperties;

  uppercase?: boolean;

  locked?: boolean;
  disabled?: boolean;

  noResultsText?: string;
  maxDropdownItems?: number;
};

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  style = {},
  uppercase = true,

  locked = false,
  disabled = false,

  noResultsText = "Sem opções",
  maxDropdownItems = 80,
}) => {
  const { theme } = useTheme();
  const primaryColor = theme?.primary || "#124280";

  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [query, setQuery] = useState("");

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isDisabled = locked || disabled;

  // ======= Estilos base (igual seu TextField) =======
  const outerContainerStyle: React.CSSProperties = {
    marginBottom: 0,
    marginLeft: style.marginLeft ?? 0,
    opacity: isDisabled ? 0.7 : 1,
  };

  const rowStyle: React.CSSProperties = {
    display: "flex",
    gap: 8,
    alignItems: "stretch",
    width: style.width ?? "100%",
  };

  const inputContainerStyle: React.CSSProperties = {
    flex: 1,
    height: "40px",
    backgroundColor: isDisabled ? "#F3F3F3" : "#FFFFFF",
    border: "1px solid #8A8A8A",
    borderRadius: 2,
    boxSizing: "border-box",
    position: "relative",
    cursor: isDisabled ? "not-allowed" : "text",
    // ✅ NÃO pode ter overflow hidden, senão o dropdown some/corta
    overflow: "visible",
  };

  const ChevronDownSVG = (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      style={{ display: "block" }}
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="#686D79"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const norm = (s: string) => (uppercase ? s.toUpperCase() : s);

  const selectedIds = useMemo(
    () => new Set<number>(value.map((v) => v.id)),
    [value]
  );

  const filteredOptions = useMemo(() => {
    const q = norm((query || "").trim());
    const base = options.map((o) => ({ ...o, label: norm(o.label) }));

    const unselected = base.filter((o) => !selectedIds.has(o.id));

    if (!q) return unselected.slice(0, maxDropdownItems);

    return unselected
      .filter((o) => o.label.includes(q))
      .slice(0, maxDropdownItems);
  }, [query, options, selectedIds, uppercase, maxDropdownItems]);

  const addOption = (opt: MultiSelectOption) => {
    if (isDisabled) return;
    if (selectedIds.has(opt.id)) return;

    const next = [...value, { id: opt.id, label: norm(opt.label) }];
    onChange(next);

    setQuery("");
    setOpen(false);

    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const removeOption = (id: number) => {
    if (isDisabled) return;
    onChange(value.filter((v) => v.id !== id));
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // ✅ click fora → fecha dropdown
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // reset foco ao abrir
  useEffect(() => {
    if (!open) {
      setFocusedIndex(-1);
      return;
    }
    setFocusedIndex(filteredOptions.length ? 0 : -1);
  }, [open, filteredOptions.length]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled) return;

    // abre com Enter/ArrowDown
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }

    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) => {
        const next = prev + 1;
        return next >= filteredOptions.length ? 0 : next;
      });
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? Math.max(0, filteredOptions.length - 1) : next;
      });
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
        addOption(filteredOptions[focusedIndex]);
      }
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }

    // backspace sem query → remove último chip
    if (e.key === "Backspace" && !query) {
      if (value.length) removeOption(value[value.length - 1].id);
      return;
    }
  };

  return (
    <div ref={rootRef} style={outerContainerStyle} onKeyDown={handleKeyDown}>
      <div style={{ marginBottom: 7, marginTop: 20 }}>
        <label
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 16,
            fontWeight: 300,
            color: "#686D79FF",
          }}
        >
          {label}
        </label>
      </div>

      <div style={rowStyle}>
        <div
          style={inputContainerStyle}
          // ✅ onMouseDown abre ANTES do blur do input (igual padrão de dropdown “bom”)
          onMouseDown={(e) => {
            if (isDisabled) return;
            // evita perder foco antes de abrir
            e.preventDefault();
            setOpen(true);
            requestAnimationFrame(() => inputRef.current?.focus());
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              height: "100%",
              padding: "0 28px 0 8px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "nowrap",
                overflow: "hidden",
              }}
            >
              {value.map((v) => (
                <div
                  key={v.id}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 8px",
                    borderRadius: 999,
                    border: "1px solid #8A8A8A",
                    background: "#ffffff",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12,
                    whiteSpace: "nowrap",
                  }}
                  title={v.label}
                >
                  <span
                    style={{
                      maxWidth: 180,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {norm(v.label)}
                  </span>

                  <button
                    type="button"
                    disabled={isDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeOption(v.id);
                    }}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      fontSize: 14,
                      lineHeight: 1,
                      color: "#686D79",
                    }}
                    aria-label={`Remover ${v.label}`}
                    title="Remover"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <input
              ref={inputRef}
              disabled={isDisabled}
              value={uppercase ? query.toUpperCase() : query}
              onFocus={() => {
                if (isDisabled) return;
                // ✅ abre ao focar também
                setOpen(true);
              }}
              onChange={(e) => {
                if (isDisabled) return;
                setQuery(
                  uppercase ? e.target.value.toUpperCase() : e.target.value
                );
                setOpen(true);
              }}
              placeholder={
                value.length === 0 ? placeholder || "Selecione..." : ""
              }
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                backgroundColor: "transparent",
                boxSizing: "border-box",
                color: isDisabled ? "#666" : "#000",
                minWidth: 80,
              }}
            />
          </div>

          {!isDisabled && (
            <div
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              {ChevronDownSVG}
            </div>
          )}

          {open && !isDisabled && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderTop: "none",
                borderRadius: "0 0 2px 2px",
                maxHeight: 220,
                overflowY: "auto",
                zIndex: 9999,
                boxShadow:
                  "0 6px 12px rgba(10, 31, 68, 0.10), 0 2px 6px rgba(10, 31, 68, 0.06)",
              }}
            >
              {filteredOptions.length === 0 ? (
                <div
                  style={{
                    padding: 10,
                    color: "#666",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 14,
                  }}
                >
                  {noResultsText}
                </div>
              ) : (
                filteredOptions.map((opt, i) => {
                  const focused = i === focusedIndex;
                  return (
                    <div
                      key={opt.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        addOption(opt);
                      }}
                      onMouseEnter={() => setFocusedIndex(i)}
                      style={{
                        padding: "8px 10px",
                        cursor: "pointer",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14,
                        backgroundColor: focused
                          ? "rgba(18,66,128,0.08)"
                          : "#fff",
                        color: "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {norm(opt.label)}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          color: primaryColor,
                          opacity: 0.9,
                        }}
                      >
                        +
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>--</div>
    </div>
  );
};

export default MultiSelectField;
